import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { AuthRequests, CaptchaResponseModel } from '@/app/api/auth';
import ModalWidget from '@/app/components/modal_widget';
import CircularProgressIndicator from '@/app/components/circular_progress_indicator';

const SERVER_PIECE_SIZE = 88;
const KNOB_RADIUS = 14;
const PIECE_IMG_SIZE = SERVER_PIECE_SIZE + KNOB_RADIUS * 2;

interface CaptchaPuzzleProps {
  isOpen: boolean;
  phoneNumber: string;
  onSuccess: () => void;
  onRateLimited: () => void;
  onClose: () => void;
}

const CaptchaPuzzle: React.FC<CaptchaPuzzleProps> = ({
  isOpen,
  phoneNumber,
  onSuccess,
  onRateLimited,
  onClose,
}) => {
  const [captcha, setCaptcha] = useState<CaptchaResponseModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sliderX, setSliderX] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ mouseX: number; sliderX: number } | null>(null);
  const sliderXRef = useRef(0);
  const containerWidthRef = useRef(0);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const w = containerRef.current.offsetWidth;
      containerWidthRef.current = w;
      setContainerWidth(w);
    }
  }, [captcha]);

  const loadCaptcha = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSliderX(0);
    sliderXRef.current = 0;
    try {
      const data = await AuthRequests.getCaptcha();
      setCaptcha(data);
    } catch {
      setCaptcha(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadCaptcha();
  }, [isOpen, loadCaptcha]);

  const getScale = () => {
    if (!containerWidth || !captcha) return 1;
    return containerWidth / captcha.imageWidth;
  };

  const submit = useCallback(async (finalSliderX: number) => {
    if (!captcha || submitting) return;
    const scale = containerWidthRef.current ? containerWidthRef.current / captcha.imageWidth : 1;
    const puzzleX = Math.round(finalSliderX / scale + KNOB_RADIUS);
    const clamped = Math.max(0, Math.min(puzzleX, captcha.imageWidth - SERVER_PIECE_SIZE));

    setSubmitting(true);
    setError(null);
    try {
      await AuthRequests.sendOtp({
        phoneNumber,
        captchaId: captcha.captchaId,
        puzzleX: clamped,
      });
      onSuccess();
    } catch (err: any) {
      if (err?.httpStatus === 429) {
        onRateLimited();
        return;
      }
      setError('Невірна позиція. Спробуйте ще раз.');
      sliderXRef.current = 0;
      setSliderX(0);
      await loadCaptcha();
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captcha, submitting, phoneNumber, onSuccess, onRateLimited, loadCaptcha]);

  const clampSlider = (x: number) => {
    if (!containerWidth || !captcha) return x;
    const scale = getScale();
    const pieceDisplayWidth = PIECE_IMG_SIZE * scale;
    const max = containerWidth - pieceDisplayWidth;
    return Math.max(0, Math.min(x, max));
  };

  const onDragStart = (clientX: number) => {
    if (submitting) return;
    dragStart.current = { mouseX: clientX, sliderX };
  };

  const onDragMove = (clientX: number) => {
    if (!dragStart.current || submitting) return;
    const delta = clientX - dragStart.current.mouseX;
    const newX = clampSlider(dragStart.current.sliderX + delta);
    sliderXRef.current = newX;
    setSliderX(newX);
  };

  const onDragEnd = () => {
    if (!dragStart.current) return;
    dragStart.current = null;
    submit(sliderXRef.current);
  };

  const handleMouseDown = (e: React.MouseEvent) => onDragStart(e.clientX);
  const handleMouseMove = (e: React.MouseEvent) => onDragMove(e.clientX);
  const handleMouseUp = () => onDragEnd();

  const handleTouchStart = (e: React.TouchEvent) => onDragStart(e.touches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => onDragMove(e.touches[0].clientX);
  const handleTouchEnd = () => onDragEnd();

  const scale = captcha && containerWidth ? containerWidth / captcha.imageWidth : 1;
  const pieceDisplayWidth = PIECE_IMG_SIZE * scale;

  return (
    <ModalWidget isOpen={isOpen} onClose={onClose}>
      <div className={styles.sheet} onClick={e => e.stopPropagation()}>
        <div className={styles.handle} />

        <div className={styles.header}>
          <span className={styles.title}>Підтвердіть, що ви людина</span>
          <button className={styles.refresh} onClick={loadCaptcha} disabled={loading}>
            ↻
          </button>
        </div>

        {loading ? (
          <div className={styles.loader}>
            <CircularProgressIndicator />
          </div>
        ) : captcha === null ? (
          <div className={styles.loader}>
            <span className={styles.errorText}>Не вдалося завантажити. Спробуйте ще раз.</span>
          </div>
        ) : (
          <div
            className={styles.puzzle}
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className={styles.imageWrap}>
              <img
                className={styles.bg}
                src={`data:image/png;base64,${captcha.backgroundImage}`}
                alt=""
                draggable={false}
              />
              <img
                className={styles.piece}
                src={`data:image/png;base64,${captcha.puzzlePiece}`}
                alt=""
                draggable={false}
                style={{
                  left: sliderX,
                  top: captcha.puzzleY * scale - KNOB_RADIUS * scale,
                  width: pieceDisplayWidth,
                  height: pieceDisplayWidth,
                }}
              />
            </div>

            <div className={styles.track}>
              <span className={styles.trackHint}>Потягніть для перевірки</span>
              <div
                className={styles.thumb}
                style={{
                  left: sliderX,
                  opacity: submitting ? 0.5 : 1,
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
              >
                {submitting ? <CircularProgressIndicator /> : '›'}
              </div>
            </div>
          </div>
        )}

        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    </ModalWidget>
  );
};

export default CaptchaPuzzle;
